import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Button, ClickAwayListener, Grid, styled } from '@mui/material';
import {
	DataGridPremium,
	GridActionsCellItem,
	GridFooterContainer,
	GridRow,
	GridRowModes,
	useGridApiRef
} from '@mui/x-data-grid-premium';
import type { GridColDef, GridRowModel, GridRowModesModel, GridSlotsComponent } from '@mui/x-data-grid-premium';

import { type ModelType } from '../../../../hooks/useModel';
import useSettings from '../../../../hooks/useSettings';
import Icon from '../../../../Icon';
import { getItemId } from '../../../../Utils';
import { NoRowsOverlay } from '../../../view/table/ViewTableComponents';
import { type ViewFooterCustomInfoProps } from '../../../view/components/ViewFooter';
import DetailsSectionWrapper, { type DetailsSectionWrapperProps } from '../../components/DetailsSectionWrapper';

export type DetailsSectionTableProps = {
	name: string;
	columns: GridColDef[];
	idField: string;
	rows?: any[];
	additionalInfo?: any;
	slots?: {
		footerCustomInfo?: React.FC<ViewFooterCustomInfoProps>;
	};
	isEdit?: boolean;
	sendChange: ModelType['sendChange'];
	editProps?: DetailsSectionTableEditProps;
	disabledAdd?: boolean;
} & Pick<DetailsSectionWrapperProps, 'className' | 'title' | 'subheader' | 'avatar' | 'headerAction'>;

type TableFooterProps = Pick<DetailsSectionTableProps, 'name' | 'isEdit' | 'sendChange'> & {
	customInfo?: React.ReactNode;
	handleRowAdd: VoidFunction;
	disabledRowAdd?: boolean;
};

type DetailsSectionTableEditProps =
	| { editMode: 'row'; newRowBaseModel?: any }
	| { editMode: 'dialog'; showEditDialog: (props: DetailsSectionTableEditDialogProps) => void };

export interface DetailsSectionTableEditDialogProps {
	model?: any;
	isAdd?: boolean;
	sendChange: (newModel: any) => void;
}

declare module '@mui/x-data-grid-premium' {
	interface FooterPropsOverrides extends TableFooterProps {}
}

const StyledDataGrid = styled(DataGridPremium)(({ theme }) => ({
	border: 0,
	fontVariantNumeric: 'tabular-nums',
	'.MuiDataGrid-cell': {
		display: 'flex',
		alignItems: 'center',
		padding: 8,
		'&.MuiDataGrid-cell--editing': {
			padding: 0,
			'&:has(div)': {
				borderTop: 0
			}
		}
	},
	'.MuiDataGrid-row--editing': {
		boxShadow: 'none'
	}
}));

function Footer(props: TableFooterProps) {
	const { t } = useSettings();

	return (
		<GridFooterContainer className="p-8" sx={theme => ({ borderTop: `1px solid ${theme.palette.divider}` })}>
			<Grid className="w-full" container justifyContent="space-between" spacing={2}>
				<Grid>{props.customInfo}</Grid>
				{props.isEdit ? (
					<Grid>
						<Button
							size="small"
							startIcon={<Icon icon="add-circle" />}
							onClick={props.handleRowAdd}
							disabled={props.disabledRowAdd}
						>
							{t('DIALOG_ACTION_ADD')}
						</Button>
					</Grid>
				) : (
					<></>
				)}
			</Grid>
		</GridFooterContainer>
	);
}

export default function DetailsSectionTable({
	rows = [],
	editProps = { editMode: 'row', newRowBaseModel: {} },
	...props
}: DetailsSectionTableProps) {
	const { t } = useSettings();
	const apiRef = useGridApiRef();

	const isDialogMode = editProps.editMode === 'dialog';
	const editFieldToFocus = props.columns[0].field;

	const [rowModesModel, setRowModesModel] = React.useState<any>({});

	const idGetter = item => getItemId(item, props.idField);

	const handleRowAdd = () => {
		const sendChange = newRow => props.sendChange(props.name, [...rows, newRow]);

		if (isDialogMode) {
			editProps.showEditDialog({ isAdd: true, sendChange });
		} else {
			const id = uuidv4();
			const newRowModel = { ...(editProps.newRowBaseModel || {}), [props.idField]: id };
			sendChange(newRowModel);
			const newRowModeModel = { mode: GridRowModes.Edit, fieldToFocus: editFieldToFocus };
			setRowModesModel((oldModel: any) => ({ ...oldModel, [id]: newRowModeModel }));
		}
	};

	const handleRowDelete = id => {
		const newRows = rows.filter(row => idGetter(row) !== id);
		props.sendChange(props.name, newRows);
	};

	const handleRowUpdate = newRow => {
		// const changes = Object.keys(newRow).filter(key => newRow[key] !== oldRow[key]);
		// if (changes.length > 0) {
		const newRows = rows.map(row => (idGetter(row) === idGetter(newRow) ? newRow : row));
		props.sendChange(props.name, newRows);
		// }
	};

	const processRowUpdate = (newRow: GridRowModel) => {
		handleRowUpdate(newRow);
		return { ...newRow };
	};

	const handleRowsModesModelChange = (newRowModesModel: GridRowModesModel) => {
		const currentRow = apiRef.current?.getRow(Object.keys(newRowModesModel)[0]);
		if (!currentRow?.___MODULESEARCH_DIALOG_OPEN___) setRowModesModel(newRowModesModel);
	};

	if (props.isEdit) {
		const getActions = ({ id, row }) =>
			isDialogMode
				? [
						<GridActionsCellItem
							icon={<Icon icon="edit" />}
							label={t('VIEW_ITEM_BUTTON_EDIT')}
							onClick={() => editProps.showEditDialog({ model: row, sendChange: handleRowUpdate })}
						/>,
						<GridActionsCellItem
							icon={<Icon icon="delete" />}
							label={t('VIEW_ITEM_BUTTON_DELETE')}
							onClick={() => handleRowDelete(id)}
						/>
					]
				: [
						<GridActionsCellItem
							icon={<Icon icon="delete" />}
							label={t('VIEW_ITEM_BUTTON_DELETE')}
							onClick={() => handleRowDelete(id)}
						/>
					];

		props.columns.push({
			field: 'actions',
			type: 'actions',
			width: isDialogMode ? 80 : 40,
			cellClassName: 'actions',
			getActions
		});
	}

	const EditableRow: GridSlotsComponent['row'] = params => (
		<ClickAwayListener
			onClickAway={() => {
				const isEditing = !!rowModesModel[params.rowId];
				// const isFocused = params.focusedColumnIndex !== undefined;
				if (isEditing) {
					const newRow = apiRef.current?.getRowWithUpdatedValues(params.rowId, 'id');
					handleRowUpdate(newRow);
				}
			}}
		>
			<GridRow {...params} />
		</ClickAwayListener>
	);

	const footerCustomInfo = props.slots?.footerCustomInfo
		? props.slots?.footerCustomInfo({ items: rows, additionalInfo: props.additionalInfo || {} })
		: undefined;

	const showFooter = footerCustomInfo || props.isEdit;

	return (
		<DetailsSectionWrapper
			avatar={props.avatar}
			title={props.title}
			subheader={props.subheader}
			headerAction={props.headerAction}
			disablePadding
		>
			<StyledDataGrid
				apiRef={apiRef}
				className={props.className}
				slots={{
					row: isDialogMode ? GridRow : EditableRow,
					footer: Footer,
					noRowsOverlay: NoRowsOverlay
				}}
				slotProps={{
					footer: {
						customInfo: footerCustomInfo,
						isEdit: props.isEdit,
						handleRowAdd,
						disabledRowAdd: props.disabledAdd
					}
				}}
				columns={props.columns}
				rows={rows}
				rowCount={rows.length}
				getRowId={idGetter}
				density="compact"
				paginationMode="server"
				sortingMode="server"
				disableVirtualization
				disableColumnMenu
				disableRowSelectionOnClick
				hideFooter={!showFooter}
				editMode="row"
				rowModesModel={!isDialogMode ? rowModesModel : undefined}
				onRowModesModelChange={!isDialogMode ? handleRowsModesModelChange : undefined}
				processRowUpdate={!isDialogMode ? processRowUpdate : undefined}
				onProcessRowUpdateError={error => console.error(error)}
			/>
		</DetailsSectionWrapper>
	);
}
